import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'

import { updateUserDetails } from 'api/auth'
import { updateAlertPreferences } from 'api/savedAlerts'
import { syncUserDetails } from 'actions/AuthActions'
import { updateLESubscriptionsV2 } from 'actions/LESubscriptionsActions'
import { SubscriptionSource } from 'api/LESubscriptions'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { useAppDispatch } from 'hooks/reduxHooks'
import { getContentSubscriptionUpdate } from 'components/Common/Alerts/alertContentSubscriptions'
import type { AlertChannelChoiceValues } from './useAlertChannelSelection'

export interface AlertChannelsSaver {
  save: (choice: AlertChannelChoiceValues) => Promise<boolean>
  saving: boolean
}

export default function useSaveAlertChannels(userId: string | undefined): AlertChannelsSaver {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const [saving, setSaving] = useState<boolean>(false)

  const save = useCallback(async(choice: AlertChannelChoiceValues): Promise<boolean> => {
    if (!userId) return false
    setSaving(true)
    try {
      if (choice.newPhoneNumber) {
        const details = await updateUserDetails({
          phone: choice.newPhoneNumber.phone,
          phonePrefix: choice.newPhoneNumber.prefix,
        })
        dispatch(syncUserDetails(details))
      }

      await updateAlertPreferences(userId, {
        emailEnabled: choice.email,
        smsEnabled: choice.sms,
      })

      if (choice.subscribeToTodaysEscapes !== undefined) {
        dispatch(updateLESubscriptionsV2({
          userId,
          subscriptions: {
            emailSubscribed: choice.email,
            smsSubscribed: choice.sms,
            ...getContentSubscriptionUpdate(choice),
          },
          source: SubscriptionSource.UNKNOWN,
        }))
      }
      return true
    } catch {
      showSnackbar(intl.formatMessage({
        id: 'tDtay7zOyjv2JTKzkNyo',
        defaultMessage: 'We couldn’t save your alert preferences. Please try again.',
        description: 'Error shown when saving delivery channels before creating a destination offer alert fails',
      }), 'critical')
      return false
    } finally {
      setSaving(false)
    }
  }, [dispatch, intl, userId])

  return { save, saving }
}
